import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../api/invoices';
import { paymentsApi } from '../api/payments';
import { companyApi } from '../api/company';
import { attachmentsApi } from '../api/attachments';
import { subscriptionApi } from '../api/subscription';
import { eInvoicingApi, ewayBillApi } from '../api/eInvoicing';
import { FeatureGate } from '../components/FeatureGate';
import RecordPaymentModal from '../components/RecordPaymentModal';
import { buildInvoiceHtml, buildLorryReceiptHtml, buildFreightBillHtml, buildAllIndividualLrsHtml } from '@coreledger/business-logic';
import type { InvoiceAttachment } from '@coreledger/shared-types';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';

const fmt = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS_CLS: Record<string, string> = { paid: 'badge-paid', unpaid: 'badge-unpaid', overdue: 'badge-overdue', draft: 'badge-draft' };
const DOC_LABELS: Record<string, string> = { tax_invoice: 'Tax Invoice', bill_of_supply: 'Bill of Supply', quotation: 'Quotation', credit_note: 'Credit Note', debit_note: 'Debit Note', lorry_receipt: 'Lorry Receipt' };

function InfoRow({ label, value }: { label: string; value?: string | number | boolean | null }) {
  if (value === null || value === undefined || value === '' || value === false) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.3rem', fontSize: '0.875rem' }}>
      <span style={{ color: 'var(--text-muted)', minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{String(value)}</span>
    </div>
  );
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const { canDelete } = useRole();

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const { data: payments = [], isLoading: loadingPayments } = useQuery({
    queryKey: ['payments', id],
    queryFn: () => paymentsApi.byInvoice(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: companyApi.get, staleTime: 30 * 1000 });
  const { data: sub } = useQuery({ queryKey: ['subscription'], queryFn: subscriptionApi.get, staleTime: 60 * 1000 });
  const canWhatsApp = !sub || (!sub.isExpired && sub.features.whatsappReminders);

  const { data: attachments = [], isLoading: loadingAttachments } = useQuery({
    queryKey: ['attachments', id],
    queryFn: () => attachmentsApi.list(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

  const [showPayModal, setShowPayModal] = useState(false);
  const [printFormat, setPrintFormat] = useState('classic');
  const autoPrinted = useRef(false);
  const autoPayForm = useRef(false);
  useEffect(() => {
    if (autoPrinted.current) return;
    if (searchParams.get('print') === 'true' && invoice && profile) {
      autoPrinted.current = true;
      handlePrint('default');
    }
  });
  useEffect(() => {
    if (autoPayForm.current) return;
    if (searchParams.get('addPayment') === 'true' && invoice) {
      autoPayForm.current = true;
      setShowPayModal(true);
    }
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const deletePayment = useMutation({
    mutationFn: (paymentId: string) => paymentsApi.delete(id!, paymentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments', id] });
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.refetchQueries({ queryKey: ['sales-summary'], type: 'all' });
      qc.refetchQueries({ queryKey: ['outstanding'], type: 'all' });
      qc.refetchQueries({ type: 'all' });
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: (attId: string) => attachmentsApi.remove(attId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attachments', id] }); qc.refetchQueries({ type: 'all' }); },
  });

  const markQuotation = useMutation({
    mutationFn: (status: 'won' | 'lost' | 'expired') => invoicesApi.update(id!, { quotationStatus: status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.refetchQueries({ type: 'all' });
    },
  });

  const generateIrn = useMutation({
    mutationFn: () => eInvoicingApi.generateIrn(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); qc.refetchQueries({ type: 'all' }); },
    onError: err => alert(err instanceof Error ? err.message : 'IRN generation failed'),
  });

  const cancelIrn = useMutation({
    mutationFn: () => eInvoicingApi.cancelIrn(id!, 'User requested'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); qc.refetchQueries({ type: 'all' }); },
  });

  const generateEwb = useMutation({
    mutationFn: () => ewayBillApi.generateEwb(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); qc.refetchQueries({ type: 'all' }); },
    onError: err => alert(err instanceof Error ? err.message : 'EWB generation failed'),
  });

  const cancelEwb = useMutation({
    mutationFn: () => ewayBillApi.cancelEwb(id!, 'User requested'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); qc.refetchQueries({ type: 'all' }); },
  });

  const extendEwb = useMutation({
    mutationFn: () => ewayBillApi.extendEwb(id!, 1),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoice', id] }); qc.refetchQueries({ type: 'all' }); },
  });

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true); setUploadError(null);
    try {
      await attachmentsApi.upload(id, file);
      qc.invalidateQueries({ queryKey: ['attachments', id] });
      qc.refetchQueries({ type: 'all' });
    } catch (err) { setUploadError(err instanceof Error ? err.message : 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  }

  function handlePrint(type: 'default' | 'freight_bill' | 'individual_lrs' = 'default') {
    if (!invoice || !profile) return;
    let html: string;
    if (type === 'individual_lrs') {
      html = buildAllIndividualLrsHtml(invoice, profile, '');
    } else if (type === 'freight_bill' || (invoice.trips && invoice.trips.length > 0 && invoice.docType !== 'lorry_receipt')) {
      html = buildFreightBillHtml(invoice, profile, '');
    } else if (invoice.docType === 'lorry_receipt') {
      html = buildLorryReceiptHtml(invoice, profile, '');
    } else {
      html = buildInvoiceHtml(invoice, profile, printFormat);
    }
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  }

  if (isLoading) return <div className="loading-overlay"><span className="spinner" /></div>;
  if (!invoice) return <div className="page"><div className="error-box">Invoice not found</div></div>;

  const paidAmount = payments.reduce((s, p) => s + p.amount, 0);
  const balanceDue = invoice.total - paidAmount - (invoice.advanceReceived ?? 0);
  const isCgst = invoice.taxType === 'cgst_sgst';
  const isLr = invoice.docType === 'lorry_receipt';
  const hasTax = invoice.taxTotal > 0;
  const tdsAmt = invoice.tdsRate ? (invoice.subtotal * invoice.tdsRate / 100) : 0;
  const tcsAmt = invoice.tcsRate ? (invoice.subtotal * invoice.tcsRate / 100) : 0;

  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      {/* Page header */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: '0.25rem' }}>
            {invoice.invoiceNumber || '(Draft)'}
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span className="badge badge-blue">{DOC_LABELS[invoice.docType ?? ''] ?? invoice.docType}</span>
            {isLr
              ? <span className={`badge ${invoice.lrStatus === 'billed' ? 'badge-paid' : 'badge-unpaid'}`}>{invoice.lrStatus === 'billed' ? 'Billed' : 'Unbilled'}</span>
              : <span className={`badge ${STATUS_CLS[invoice.status] ?? 'badge-gray'}`}>{invoice.status}</span>
            }
            {invoice.docType === 'quotation' && invoice.quotationStatus && (
              <span className={`badge ${invoice.quotationStatus === 'won' ? 'badge-paid' : invoice.quotationStatus === 'lost' ? 'badge-overdue' : 'badge-gray'}`}>
                {invoice.quotationStatus === 'won' ? '✓ Won' : invoice.quotationStatus === 'lost' ? '✗ Lost' : invoice.quotationStatus === 'expired' ? 'Expired' : 'Pending'}
              </span>
            )}
            {invoice.isRcm && <span className="badge badge-gray">RCM</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={() => navigate('/invoices')}>← Back</button>
          <button className="btn btn-ghost" title="Clone this invoice with today's date" onClick={() => {
            sessionStorage.setItem('cl_use_template', JSON.stringify({
              ...invoice,
              id: undefined, invoiceNumber: '', status: 'unpaid', paidAmount: undefined,
              date: new Date().toISOString().slice(0, 10),
              dueDate: undefined,
            }));
            navigate('/invoices/new');
          }}>🔁 Duplicate</button>
          {invoice.docType === 'quotation' && (
            <button className="btn btn-ghost" style={{ borderColor: 'var(--success)', color: 'var(--success)' }} onClick={() => {
              sessionStorage.setItem('cl_use_template', JSON.stringify({
                ...invoice, docType: 'tax_invoice', status: 'unpaid',
                invoiceNumber: '', id: undefined, paidAmount: undefined,
              }));
              navigate('/invoices/new');
            }}>
              Convert to Invoice
            </button>
          )}
          {invoice.docType === 'quotation' && invoice.quotationStatus !== 'won' && (
            <button
              className="btn btn-ghost"
              style={{ borderColor: 'var(--success)', color: 'var(--success)' }}
              disabled={markQuotation.isPending}
              onClick={() => markQuotation.mutate('won')}
            >✓ Mark Won</button>
          )}
          {invoice.docType === 'quotation' && invoice.quotationStatus !== 'lost' && (
            <button
              className="btn btn-ghost"
              style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
              disabled={markQuotation.isPending}
              onClick={() => markQuotation.mutate('lost')}
            >✗ Mark Lost</button>
          )}
          {!isLr && (
            <select value={printFormat} onChange={e => setPrintFormat(e.target.value)} style={{ width: 'auto', fontSize: '0.85rem' }}>
              <option value="classic">Classic</option>
              <option value="modern">Modern</option>
              <option value="minimal">Minimal</option>
              <option value="elegant">Elegant</option>
              <option value="bold">Bold</option>
            </select>
          )}
          <button className="btn btn-ghost" onClick={() => handlePrint('default')} disabled={!profile}>Print / PDF</button>
          {isLr && invoice?.trips && invoice.trips.length > 0 && (
            <button className="btn btn-ghost" onClick={() => handlePrint('individual_lrs')} disabled={!profile} title="Print each trip as a separate LR">All LRs</button>
          )}
          {!isLr && invoice?.trips && invoice.trips.length > 0 && (
            <button className="btn btn-ghost" onClick={() => handlePrint('freight_bill')} disabled={!profile} title="Print as Freight Bill with trip details">Freight Bill</button>
          )}
          {canDelete && <button className="btn btn-primary" onClick={() => navigate(isLr ? `/lorry-receipts/${id}/edit` : `/invoices/${id}/edit`)}>Edit</button>}
        </div>
      </div>

      {/* Company + Party */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="form-row form-row-2" style={{ gap: '1.5rem', padding: '1.25rem', marginBottom: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>From</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>{profile?.name ?? '—'}</div>
            {profile?.gstin && <div style={{ fontSize: '0.83rem', color: 'var(--text-2)', fontFamily: 'monospace' }}>GSTIN: {profile.gstin}</div>}
            {profile?.addressLine1 && <div style={{ fontSize: '0.83rem', color: 'var(--text-2)', marginTop: '0.2rem' }}>{[profile.addressLine1, profile.city, profile.state, profile.postalCode].filter(Boolean).join(', ')}</div>}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Bill To</div>
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.25rem' }}>{invoice.partyName}</div>
            {invoice.partyGstin && <div style={{ fontSize: '0.83rem', color: 'var(--text-2)', fontFamily: 'monospace' }}>GSTIN: {invoice.partyGstin}</div>}
            {invoice.partyAddress && <div style={{ fontSize: '0.83rem', color: 'var(--text-2)', marginTop: '0.2rem' }}>{invoice.partyAddress}</div>}
          </div>
        </div>
      </div>

      {/* Invoice details */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Invoice Details</span></div>
        <div className="form-row form-row-2" style={{ padding: '1.25rem', marginBottom: 0 }}>
          <div>
            <InfoRow label="Invoice Date" value={invoice.date} />
            <InfoRow label="Due Date" value={invoice.dueDate} />
            {(invoice.docType === 'credit_note' || invoice.docType === 'debit_note')
              ? <InfoRow label="Invoice Reference" value={invoice.referenceNumber} />
              : <InfoRow label="Reference #" value={invoice.referenceNumber} />}
            <InfoRow label="Place of Supply" value={invoice.placeOfSupply} />
            <InfoRow label="GST Type" value={isCgst ? 'CGST + SGST (Intra-state)' : 'IGST (Inter-state)'} />
          </div>
          <div>
            <InfoRow label="Currency" value={invoice.currency && invoice.currency !== 'INR' ? invoice.currency : undefined} />
            <InfoRow label="Exchange Rate" value={invoice.exchangeRate ? `1 ${invoice.currency} = ₹${invoice.exchangeRate}` : undefined} />
            <InfoRow label="LR Refs" value={invoice.lrRefs} />
            <InfoRow label="Reverse Charge (RCM)" value={invoice.isRcm ? 'Yes' : undefined} />
          </div>
        </div>
        {invoice.termsAndConditions && (
          <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Terms & Conditions</div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{invoice.termsAndConditions}</div>
          </div>
        )}
      </div>

      {/* Transport / LR section */}
      {(invoice.vehicleNumber || invoice.lrNumber || invoice.fromCity || invoice.driverName || isLr) && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><span style={{ fontWeight: 700 }}>Transport Details</span></div>
          <div className="form-row form-row-2" style={{ padding: '1.25rem', marginBottom: 0 }}>
            <div>
              <InfoRow label="Vehicle Number" value={invoice.vehicleNumber} />
              <InfoRow label="LR Number" value={invoice.lrNumber} />
              <InfoRow label="Driver Name" value={invoice.driverName} />
            </div>
            <div>
              <InfoRow label="From" value={invoice.fromCity} />
              <InfoRow label="To" value={invoice.toCity} />
            </div>
          </div>
          {(invoice.consigneeName || invoice.consigneeGstin) && (
            <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Consignee (Ship To)</div>
              <InfoRow label="Name" value={invoice.consigneeName} />
              <InfoRow label="GSTIN" value={invoice.consigneeGstin} />
              <InfoRow label="Address" value={invoice.consigneeAddress} />
            </div>
          )}
        </div>
      )}

      {/* Line items */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Line Items</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Description</th>
                <th style={{ width: 80 }}>HSN/SAC</th>
                <th style={{ width: 70 }}>Qty</th>
                <th style={{ width: 100 }}>Rate</th>
                <th style={{ width: 110 }}>Taxable</th>
                <th style={{ width: 70 }}>GST %</th>
                {hasTax && (isCgst
                  ? <><th style={{ width: 90 }}>CGST</th><th style={{ width: 90 }}>SGST</th></>
                  : <th style={{ width: 100 }}>IGST</th>)}
                <th style={{ width: 110, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, idx) => {
                const gstAmt = item.taxableValue * item.gstRate / 100;
                return (
                  <tr key={item.id}>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{idx + 1}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{item.description}</div>
                      {item.notes && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{item.notes}</div>}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{item.hsnSac ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{item.qty}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(item.rate)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt(item.taxableValue)}</td>
                    <td style={{ textAlign: 'center' }}>{item.gstRate}%</td>
                    {hasTax && (isCgst
                      ? <><td style={{ textAlign: 'right' }}>{fmt(gstAmt / 2)}</td><td style={{ textAlign: 'right' }}>{fmt(gstAmt / 2)}</td></>
                      : <td style={{ textAlign: 'right' }}>{fmt(gstAmt)}</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(item.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1.25rem' }}>
          <table style={{ width: 320 }}>
            <tbody>
              <tr>
                <td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>Subtotal (taxable)</td>
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt(invoice.subtotal)}</td>
              </tr>
              {hasTax && (isCgst
                ? <>
                    <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>CGST</td><td style={{ textAlign: 'right' }}>{fmt(invoice.taxTotal / 2)}</td></tr>
                    <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>SGST</td><td style={{ textAlign: 'right' }}>{fmt(invoice.taxTotal / 2)}</td></tr>
                  </>
                : <tr><td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>IGST</td><td style={{ textAlign: 'right' }}>{fmt(invoice.taxTotal)}</td></tr>)}
              {tdsAmt > 0 && (
                <tr>
                  <td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>TDS ({invoice.tdsRate}% {invoice.tdsSection ? `— ${invoice.tdsSection}` : ''})</td>
                  <td style={{ textAlign: 'right', color: 'var(--danger)' }}>− {fmt(tdsAmt)}</td>
                </tr>
              )}
              {tcsAmt > 0 && (
                <tr>
                  <td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>TCS ({invoice.tcsRate}%)</td>
                  <td style={{ textAlign: 'right' }}>{fmt(tcsAmt)}</td>
                </tr>
              )}
              {(invoice.advanceReceived ?? 0) > 0 && (
                <tr>
                  <td style={{ color: 'var(--text-2)', padding: '0.2rem 0' }}>Advance Received ({invoice.advanceMode ?? ''})</td>
                  <td style={{ textAlign: 'right', color: 'var(--danger)' }}>− {fmt(invoice.advanceReceived!)}</td>
                </tr>
              )}
              <tr style={{ borderTop: '2px solid var(--border)' }}>
                <td style={{ fontWeight: 700, padding: '0.4rem 0 0' }}>Invoice Total</td>
                <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '1.1rem', paddingTop: '0.4rem', color: 'var(--primary)' }}>{fmt(invoice.total)}</td>
              </tr>
              {paidAmount > 0 && (
                <tr>
                  <td style={{ color: 'var(--success)', padding: '0.2rem 0', fontSize: '0.875rem' }}>Paid</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)', fontSize: '0.875rem' }}>− {fmt(paidAmount)}</td>
                </tr>
              )}
              {balanceDue !== invoice.total && (
                <tr style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ fontWeight: 700, padding: '0.3rem 0 0', color: balanceDue > 0 ? 'var(--danger)' : 'var(--success)' }}>Balance Due</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, paddingTop: '0.3rem', color: balanceDue > 0 ? 'var(--danger)' : 'var(--success)' }}>{fmt(Math.abs(balanceDue))}{balanceDue < 0 ? ' (over)' : ''}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Share section */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header"><span style={{ fontWeight: 700 }}>Share Invoice</span></div>
        <div style={{ padding: '1rem 1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {canWhatsApp ? (
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Hi ${invoice.partyName},\n\nPlease find your invoice ${invoice.invoiceNumber} for ₹${invoice.total.toLocaleString('en-IN')} dated ${invoice.date}.\n\nThank you!`)}`}
              target="_blank" rel="noreferrer"
              className="btn btn-ghost"
              style={{ color: '#25d366', borderColor: '#25d366', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
          ) : (
            <span className="btn btn-ghost" style={{ color: 'var(--text-muted)', borderColor: 'var(--border)', cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }} title="Upgrade to Basic or higher to enable WhatsApp sharing">
              🔒 WhatsApp
            </span>
          )}
          <a
            href={`mailto:?subject=Invoice ${invoice.invoiceNumber} — ₹${invoice.total.toLocaleString('en-IN')}&body=Hi ${invoice.partyName},%0A%0APlease find your invoice ${invoice.invoiceNumber} for ₹${invoice.total.toLocaleString('en-IN')} dated ${invoice.date}.%0A%0AThank you!`}
            className="btn btn-ghost"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
          >
            ✉ Email
          </a>
          <button className="btn btn-ghost" onClick={() => handlePrint('default')} disabled={!profile} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* Payment reminder panel */}
      {invoice.status === 'overdue' && invoice.docType !== 'quotation' && (
        <div className="card" style={{ marginBottom: '1rem', border: '1.5px solid var(--danger)' }}>
          <div className="card-header" style={{ background: 'var(--row-tint-rose)' }}>
            <span style={{ fontWeight: 700, color: 'var(--danger)' }}>🔔 Send Payment Reminder</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--danger)' }}>Overdue by {invoice.dueDate ? `${Math.max(0, Math.floor((Date.now() - new Date(invoice.dueDate).getTime()) / 86400000))} days` : 'unknown'}</span>
          </div>
          <div style={{ padding: '1rem 1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {(() => {
              const balance = fmt(invoice.total - (invoice.paidAmount ?? 0));
              const msg = `Dear ${invoice.partyName},\n\nThis is a payment reminder for invoice ${invoice.invoiceNumber ?? ''} dated ${invoice.date} for ${balance}${invoice.dueDate ? `, which was due on ${invoice.dueDate}` : ''}.\n\nKindly arrange payment at the earliest to avoid any inconvenience.\n\nThank you!`;
              const emailBody = encodeURIComponent(msg);
              const waMsg = encodeURIComponent(msg);
              return (
                <>
                  {canWhatsApp ? (
                    <a href={`https://wa.me/?text=${waMsg}`} target="_blank" rel="noreferrer"
                      className="btn btn-ghost"
                      style={{ color: '#25d366', borderColor: '#25d366' }}>
                      📱 WhatsApp Reminder
                    </a>
                  ) : (
                    <span className="btn btn-ghost" style={{ color: 'var(--text-muted)', borderColor: 'var(--border)', cursor: 'default' }} title="Upgrade to Basic or higher">
                      🔒 WhatsApp
                    </span>
                  )}
                  <a href={`mailto:?subject=Payment Reminder: Invoice ${invoice.invoiceNumber ?? ''} — ${balance}&body=${emailBody}`}
                    className="btn btn-ghost">
                    ✉ Email Reminder
                  </a>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* LR Billing Status — shown instead of Payments for lorry receipts */}
      {isLr && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><span style={{ fontWeight: 700 }}>Billing Status</span></div>
          <div style={{ padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span className={`badge ${invoice.lrStatus === 'billed' ? 'badge-paid' : 'badge-unpaid'}`} style={{ fontSize: '0.95rem', padding: '0.35rem 0.9rem' }}>
              {invoice.lrStatus === 'billed' ? 'Billed' : 'Unbilled'}
            </span>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              {invoice.lrStatus === 'billed'
                ? 'This LR has been consolidated into a sales invoice.'
                : 'This LR has not been billed yet. Select it from the Lorry Receipts page to raise an invoice.'}
            </span>
          </div>
        </div>
      )}

      {/* Payments section */}
      {invoice.docType !== 'quotation' && !isLr && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <span style={{ fontWeight: 700 }}>Payments</span>
            {invoice.status !== 'paid' && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowPayModal(true)}>+ Receipt</button>
            )}
          </div>

          {/* Payment history */}
          {loadingPayments ? (
            <div style={{ padding: '1rem', textAlign: 'center' }}><span className="spinner" /></div>
          ) : payments.length === 0 ? (
            <div style={{ padding: '1.25rem', color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>No payments recorded yet</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Mode</th><th>Reference</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td>{p.paymentDate}</td>
                    <td style={{ textTransform: 'capitalize' }}>{(p.mode ?? '').replace('_', ' ')}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{p.reference ?? '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success)' }}>{fmt(p.amount)}</td>
                    <td>
                      {canDelete && (
                        <button
                          onClick={async () => { if (await confirm({ message: 'Delete this payment?', danger: true })) deletePayment.mutate(p.id); }}
                          style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem' }}
                          title="Delete payment"
                        >×</button>
                      )}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                  <td colSpan={3} style={{ textAlign: 'right', padding: '0.4rem 0.75rem' }}>Total Paid</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)', padding: '0.4rem 0.75rem' }}>{fmt(paidAmount)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {showPayModal && id && (
        <RecordPaymentModal
          invoiceId={id}
          invoiceNumber={invoice.invoiceNumber ?? undefined}
          balanceDue={balanceDue}
          onClose={() => setShowPayModal(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['payments', id] });
            qc.invalidateQueries({ queryKey: ['invoice', id] });
            qc.refetchQueries({ type: 'all' });
          }}
        />
      )}

      {/* Attachments section */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header">
          <span style={{ fontWeight: 700 }}>Attachments</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {uploading && <span className="spinner" style={{ width: 14, height: 14 }} />}
            <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              + Upload File
            </button>
            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" />
          </div>
        </div>
        {uploadError && <div className="error-box" style={{ margin: '0.75rem 1.25rem' }}>{uploadError}</div>}
        {loadingAttachments ? (
          <div style={{ padding: '1rem', textAlign: 'center' }}><span className="spinner" /></div>
        ) : attachments.length === 0 ? (
          <div style={{ padding: '1.25rem', color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>No attachments yet — upload PDFs, images, or documents</div>
        ) : (
          <div style={{ padding: '0.5rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {attachments.map((att: InvoiceAttachment) => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: 6, background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '1.2rem' }}>{att.filename.match(/\.(pdf)$/i) ? '📄' : att.filename.match(/\.(png|jpg|jpeg|gif|webp)$/i) ? '🖼' : '📎'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{(att.filesize / 1024).toFixed(1)} KB · {att.createdAt.slice(0, 10)}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => attachmentsApi.download(att.id, att.filename)}>Download</button>
                {canDelete && (
                  <button
                    onClick={async () => { if (await confirm({ message: `Delete ${att.filename}?`, danger: true })) deleteAttachment.mutate(att.id); }}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem' }}
                  >×</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* E-Invoicing (IRN) + E-Way Bill — only for tax invoices */}
      {(invoice.docType === 'tax_invoice' || invoice.docType === 'bill_of_supply') && (
        <div className="form-row form-row-2" style={{ marginTop: '1rem', marginBottom: 0 }}>
          <FeatureGate feature="eInvoicing">
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700 }}>E-Invoice (IRN)</span>
                {invoice.irnStatus !== 'generated' && (
                  <button className="btn btn-primary btn-sm" onClick={() => generateIrn.mutate()} disabled={generateIrn.isPending}>
                    {generateIrn.isPending ? 'Generating…' : 'Generate IRN'}
                  </button>
                )}
                {invoice.irnStatus === 'generated' && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => cancelIrn.mutate()} disabled={cancelIrn.isPending}>Cancel IRN</button>
                )}
              </div>
              <div style={{ padding: '1rem 1.25rem', fontSize: '0.875rem' }}>
                {!invoice.irn ? (
                  <span style={{ color: 'var(--text-muted)' }}>No IRN generated yet</span>
                ) : (
                  <>
                    <div><span style={{ color: 'var(--text-muted)', marginRight: 8 }}>IRN:</span><span style={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{invoice.irn}</span></div>
                    <div style={{ marginTop: '0.35rem' }}><span style={{ color: 'var(--text-muted)', marginRight: 8 }}>Ack No:</span>{invoice.ackNo}</div>
                    <div><span style={{ color: 'var(--text-muted)', marginRight: 8 }}>Ack Date:</span>{invoice.ackDate}</div>
                    <div style={{ marginTop: '0.35rem' }}>
                      <span className={`badge ${invoice.irnStatus === 'generated' ? 'badge-paid' : 'badge-gray'}`}>{invoice.irnStatus}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </FeatureGate>
          <FeatureGate feature="eWayBill">
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700 }}>E-Way Bill</span>
                {invoice.ewbStatus !== 'active' && (
                  <button className="btn btn-primary btn-sm" onClick={() => generateEwb.mutate()} disabled={generateEwb.isPending}>
                    {generateEwb.isPending ? 'Generating…' : 'Generate EWB'}
                  </button>
                )}
                {invoice.ewbStatus === 'active' && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => extendEwb.mutate()} disabled={extendEwb.isPending}>+1 Day</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => cancelEwb.mutate()} disabled={cancelEwb.isPending}>Cancel</button>
                  </div>
                )}
              </div>
              <div style={{ padding: '1rem 1.25rem', fontSize: '0.875rem' }}>
                {!invoice.ewbNo ? (
                  <span style={{ color: 'var(--text-muted)' }}>No EWB generated yet{invoice.total < 50000 ? ' (required for consignments ≥ ₹50,000)' : ''}</span>
                ) : (
                  <>
                    <div><span style={{ color: 'var(--text-muted)', marginRight: 8 }}>EWB No:</span><span style={{ fontFamily: 'monospace' }}>{invoice.ewbNo}</span></div>
                    <div style={{ marginTop: '0.35rem' }}><span style={{ color: 'var(--text-muted)', marginRight: 8 }}>Valid Until:</span><span style={{ fontWeight: 600 }}>{invoice.ewbValidUpto}</span></div>
                    <div style={{ marginTop: '0.35rem' }}>
                      <span className={`badge ${invoice.ewbStatus === 'active' ? 'badge-paid' : 'badge-gray'}`}>{invoice.ewbStatus}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </FeatureGate>
        </div>
      )}
    </div>
  );
}
